import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CalendarWidget = ({ activeDates = [], scheduledEvents = [] }) => {
    const today = new Date();
    const currentMonth = today.toLocaleString('default', { month: 'short', year: 'numeric' });
    
    const daysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    const days = useMemo(() => {
        const month = today.getMonth();
        const year = today.getFullYear();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const result = [];
        
        for (let i = 1; i <= daysInMonth; i++) {
            const dateStr = `${year}-${month + 1}-${i}`;
            
            // Find all events for this specific day
            const dayEvents = (scheduledEvents || []).filter(event => {
                const eventDate = new Date(event.scheduled_at);
                return eventDate.getFullYear() === year && 
                       eventDate.getMonth() === month && 
                       eventDate.getDate() === i;
            });

            result.push({
                d: i,
                active: activeDates.includes(dateStr),
                current: i === today.getDate(),
                events: dayEvents
            });
        }
        return result;
    }, [activeDates, scheduledEvents]);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-[11px] font-normal text-slate-900">Calendar</h3>
                <span className="text-[11px] font-normal text-emerald-700 tracking-wider">{currentMonth}</span>
            </div>

            <div className="grid grid-cols-7 gap-y-3.5 text-center">
                {daysOfWeek.map((day, idx) => (
                    <span key={idx} className="text-[10px] font-normal text-slate-300">{day}</span>
                ))}
                
                {days.map((day, idx) => (
                    <div key={idx} className="flex flex-col items-center justify-center relative py-1 group">
                        <span className={`text-[12px] font-normal ${day.current ? 'text-white' : 'text-slate-600'} relative z-10`}>
                            {day.d}
                        </span>
                        
                        {day.current && (
                            <div className="absolute inset-0 m-auto w-6 h-6 bg-emerald-600 rounded-lg shadow-md shadow-emerald-200" />
                        )}
                        
                        {(day.active || day.events.length > 0) && !day.current && (
                            <div className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${day.events.length > 0 ? 'bg-emerald-600' : 'bg-emerald-500'}`} />
                        )}

                        {/* Tooltip for Scheduled Events */}
                        {day.events.length > 0 && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                <div className="bg-slate-900 text-white rounded-xl p-3 shadow-xl border border-slate-700/50">
                                    <p className="text-[9px] font-normal text-slate-400 tracking-wider mb-2 border-b border-slate-800 pb-1.5">
                                        Schedule • {day.d} {today.toLocaleString('default', { month: 'short' })}
                                    </p>
                                    <div className="space-y-2">
                                        {day.events.map((event, eIdx) => (
                                            <div key={eIdx} className="flex flex-col gap-0.5 border-l-2 border-emerald-500 pl-2">
                                                <div className="flex items-center gap-1.5">
                                                    <p className="text-[11px] font-normal text-white leading-tight tracking-tight">{event.type === 'plan' ? event.plan_name : (event.title || 'Mock Interview')}</p>
                                                    {event.type === 'plan' ? (
                                                        <span className="text-[7px] font-normal bg-emerald-500 text-white px-1 py-0.5 rounded-sm">Plan</span>
                                                    ) : event.type === 'assignment' ? (
                                                        <span className="text-[7px] font-normal bg-indigo-600 text-white px-1 py-0.5 rounded-sm">Assigned</span>
                                                    ) : (
                                                        <span className="text-[7px] font-normal bg-emerald-600 text-white px-1 py-0.5 rounded-sm">Live</span>
                                                    )}
                                                </div>
                                                <p className="text-[9px] text-slate-400 font-normal">
                                                    {new Date(event.scheduled_at?.Time || event.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45 -mt-1" />
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CalendarWidget;
